import { FC, KeyboardEvent } from 'react';
import { CircularProgress, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import {
  RoutineActivity,
  RoutineActivityRecurring,
  RoutineActivityRecurringDay,
  RoutineCustomRecurrence,
} from '@demind-inc/core';

import './RoutinesSection.scss';
import { formatTimeFromHHMM } from '../../../helpers';
import { useGeneralSettings } from '../../../hooks';
import { isCustomRecurring } from '../../Scheduler/common';
import { ReactComponent as EditIcon } from '../../../assets/tabs/routine-tab/edit-icon.svg';
import { ReactComponent as ClockIcon } from '../../../assets/tabs/routine-tab/clock-icon.svg';
import { useTranslation } from 'react-i18next';
import { DataTFunction } from '../../DataView/i18n';

interface RoutinesSectionProps {
  routines: RoutineActivity[];
  isLoading?: boolean;
  onAddRoutineClick?: () => void;
  onRoutineClick?: (activity: RoutineActivity) => void;
}

const RECURRING_DAYS: RoutineActivityRecurringDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const WEEKDAY_DAYS: RoutineActivityRecurringDay[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

const WEEKEND_DAYS: RoutineActivityRecurringDay[] = ['saturday', 'sunday'];

const dayToLabel = (t: DataTFunction, day: RoutineActivityRecurringDay) =>
  t(`routine.weekdays.short.${day}`);

const ordinalSuffix = (n: number) => {
  if (n >= 11 && n <= 13) return 'th';
  switch (n % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const formatMonthDate = (t: DataTFunction, day: number) =>
  t('routine.recurrence.dayOfMonth', { day, ordinalSuffix: ordinalSuffix(day) });

const getWeekPositionLabel = (t: DataTFunction, position: number) =>
  t(`routine.recurrence.weekPositionsInSentence.${position}`);

const formatRoutineDuration = (t: DataTFunction, min: number) => {
  const hours = Math.floor(min / 60);
  const minutes = min % 60;

  if (hours === 0) {
    return t('data.common.minutesValue', { minutes });
  }

  if (minutes === 0) {
    return t('data.common.hoursValue', { hours });
  }

  return t('data.common.durationHoursMinutes', { hours, minutes });
};

const getCustomRecurringLabel = (t: DataTFunction, recurring: RoutineCustomRecurrence) => {
  const intervalPrefix = (unit: 'week' | 'month') =>
    recurring.interval === 1
      ? t(`routine.recurrence.every.${unit}`)
      : t(`routine.recurrence.every.${unit}WithCount`, { count: recurring.interval });

  if (recurring.type === 'weekly') {
    if (recurring.daysOfWeek.length === 7) {
      return recurring.interval === 1
        ? t('routine.recurrence.everyday')
        : t('routine.recurrence.every.weekWithCount', { count: recurring.interval });
    }
    const days = recurring.daysOfWeek.map((day) => dayToLabel(t, day)).join(', ');
    return `${intervalPrefix('week')} · ${days}`;
  }

  if (recurring.type === 'monthly-date') {
    const dates = recurring.daysOfMonth.map((date) => formatMonthDate(t, date)).join(', ');
    return `${intervalPrefix('month')} · ${dates}`;
  }

  if (recurring.type === 'monthly-weekday') {
    const pos = getWeekPositionLabel(t, recurring.weekOfMonth);
    const day = dayToLabel(t, recurring.dayOfWeek);
    return `${intervalPrefix('month')} · ${pos} ${day}`;
  }

  return t('routine.recurrence.custom');
};

const getLegacyRecurringLabel = (t: DataTFunction, recurring: RoutineActivityRecurring) => {
  const enabledDays = RECURRING_DAYS.filter((day) => recurring[day]?.enabled);

  if (enabledDays.length === 7) return t('routine.recurrence.everyday');

  const isWeekdays = WEEKDAY_DAYS.every((day) => enabledDays.includes(day));
  if (enabledDays.length === 5 && isWeekdays) return t('routine.recurrence.weekdays');

  const isWeekends = WEEKEND_DAYS.every((day) => enabledDays.includes(day));
  if (enabledDays.length === 2 && isWeekends) return t('routine.recurrence.weekends');

  if (enabledDays.length === 0) return t('routine.recurrence.noRecurrence');

  return enabledDays.map((day) => dayToLabel(t, day)).join(', ');
};

const getRecurringLabel = (t: DataTFunction, activity: RoutineActivity) => {
  if (isCustomRecurring(activity.recurring)) {
    return getCustomRecurringLabel(t, activity.recurring);
  }
  return getLegacyRecurringLabel(t, activity.recurring);
};

const RoutinesSection: FC<RoutinesSectionProps> = ({
  routines,
  isLoading = false,
  onAddRoutineClick,
  onRoutineClick,
}) => {
  const { t } = useTranslation();
  const {
    generalSettings: { timeFormat },
  } = useGeneralSettings();

  const getFormattedTime = (time: string) => {
    return formatTimeFromHHMM(time, timeFormat);
  };

  const handleRoutineCardKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    activity: RoutineActivity
  ) => {
    if (!onRoutineClick) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onRoutineClick(activity);
    }
  };

  return (
    <section className="routines-section">
      <Typography variant="h3" className="routines-section__title">
        {t('routine.routines')}
      </Typography>

      <div className="routines-section__content">
        {isLoading ? (
          <div className="routines-section__state">
            <CircularProgress size={24} />
          </div>
        ) : (
          <div className="routines-section__grid">
            {routines.map((activity) => {
              const durationLabel = activity.durationMin
                ? formatRoutineDuration(t, activity.durationMin)
                : '';
              const recurringLabel = getRecurringLabel(t, activity);
              const { startTime, endTime } = activity;

              return (
                <article
                  key={activity.routineActivityId}
                  className="routines-section__card"
                  role="button"
                  tabIndex={0}
                  onClick={() => onRoutineClick?.(activity)}
                  onKeyDown={(event) => handleRoutineCardKeyDown(event, activity)}
                >
                  <div className="routines-section__card-main">
                    <Typography variant="body1" className="routines-section__card-title">
                      {activity.name}
                    </Typography>

                    {startTime && endTime && (
                      <div className="routines-section__time-row">
                        <div className="routines-section__time-pill">
                          <Typography
                            variant="subtitle2"
                            className="routines-section__time-pill-text"
                          >
                            {getFormattedTime(startTime)}
                          </Typography>
                        </div>

                        <Typography
                          variant="subtitle2"
                          className="routines-section__time-separator"
                        >
                          :
                        </Typography>

                        <div className="routines-section__time-pill">
                          <Typography
                            variant="subtitle2"
                            className="routines-section__time-pill-text"
                          >
                            {getFormattedTime(endTime)}
                          </Typography>
                        </div>

                        <EditIcon className="routines-section__icon" />
                      </div>
                    )}

                    <Typography variant="body2" className="routines-section__recurring-label">
                      {recurringLabel}
                    </Typography>
                  </div>

                  <div className="routines-section__duration-row">
                    <Typography variant="subtitle2" className="routines-section__duration-label">
                      {durationLabel}
                    </Typography>
                    <ClockIcon className="routines-section__icon" />
                  </div>
                </article>
              );
            })}

            <button
              type="button"
              className="routines-section__add-card"
              onClick={onAddRoutineClick}
            >
              <Add className="routines-section__add-card-icon" />
              <Typography variant="body1" className="routines-section__add-card-label">
                {t('routine.addRoutine')}
              </Typography>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default RoutinesSection;
